import { IconButton, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import useScopedCartActions from '../../../hooks/cartScope/useScopedCartActions.hooks';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { BLOCKED_AFFORDANCE_STYLE } from '../../../utils/agentModeAffordance';
import { CartItem, CartModifierGroup } from '../../../utils/cart';
import Colors from '../../../utils/color';
import { checkItemInCartItemTree } from '../../../utils/menu';
import { MOD_GROUP_ROW_INDENT_PX } from '../components/ModGroupRow';
import { COUNT_CHIP_STYLE } from '../components/countChip.styles';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { useDisplayPreference } from '../../Layout/DisplayPreferenceContext';
import { useAppSelector } from '../../../app/hooks';
import { selectModGroupPrompt } from '../../../redux/features/experiments/experiments.selector';
import useTTSMessage from '../../../hooks/useTTSMessage.hooks';
import { sortAndFilterMenuItems } from '../util/helper';
import { SelectMenuItemModifier } from './modGroup.props';
import { applyModGroupDefaults } from './modGroup.utils';
import ModGroup from './ModGroup';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import {
  addToSlots,
  describeRepeatedSlots,
  fillEmptySlots,
  paintSlots,
  removeFromSlots,
  buildFollowUpSets,
  getSlotParentModifier,
  INAPPLICABLE_SLOT,
  isSingleChoiceGroup,
  optionOpensGroups,
  RepeatedSlotSet,
  SlotValue,
} from '../util/repeatedSlots.utils';
import useRepeatedSlotsWrite, {
  RepeatedSlotChange,
} from './useRepeatedSlotsWrite.hooks';

/**
 * The repeated-slot panel: one panel in place of N interchangeable single-pick
 * rows (Baker's Dozen `bagel_choice_1..13`).
 *
 * - Tiles: a tap adds one of that option to the next empty slot AND makes it
 *   the brush. `×N` shows how many slots hold it; `−` takes one away.
 * - Strip: one cell per slot. A click paints that cell with the brush; a drag
 *   across cells paints every cell it crosses, committed as ONE write when the
 *   pointer lifts (HITL-1719).
 * - Keys 1–9 tap the first nine tiles, but only while the panel has focus —
 *   the agent clicks in, the panel never takes focus itself, and a digit typed
 *   anywhere else (the OmniBar quantity) never reaches it (HITL-1719).
 * - Fill all: every empty slot gets the brush; on a full panel it reads
 *   "Set all" and puts the brush in every slot (HITL-1730).
 * - Undo: back one gesture per press, as far back as the panel's first
 *   gesture on this line.
 * - Cells never move: a pick stays in the slot it was put in (HITL-1731).
 * - The `(N / 13)` beside the name and every empty cell stay red until the set
 *   is full, because sendOrder drops the whole item while any required slot is
 *   empty.
 *
 * Slots are read from the cart on every render, never cached, so whatever the
 * AI or another surface puts in them shows up as it lands. Every gesture is
 * one write through useRepeatedSlotsWrite (one dispatch, one intervention).
 *
 * Styling is inline, from the 7.x centre pane's own values (ModGroupRow,
 * OptionDrawer, ModOptionRow), so no JSS sheet is added.
 */

const FLASH_MS = 600;
const UNDO_LIMIT = 50;

const styles = {
  // The same 4px a side ModGroup's `modGroupCompact` gives every row. A
  // margin, not padding: CartItemDetail's tab panel forces
  // `padding: 0 !important` on each of its direct children, which this root is.
  root: { margin: '0 4px', outline: 'none' } as React.CSSProperties,
  // Then the rows' caret column (8px caret + 4px gap), so the label lines up
  // with every other group's name and the tiles sit off the card edge.
  // Mirrored on the right. On an inner box, where the tab panel's rule
  // cannot reach.
  body: {
    padding: `3px ${MOD_GROUP_ROW_INDENT_PX}px`,
  } as React.CSSProperties,
  // HITL-1722 follow-ups: no horizontal inset, so the strip sits exactly
  // under the parent's; the label and tiles take the drawer's 2px rail plus
  // the rest of the 12px indent instead.
  followUpRoot: { outline: 'none' } as React.CSSProperties,
  followUpBody: { padding: '3px 0' } as React.CSSProperties,
  followUpRail: {
    borderLeft: `2px solid ${Colors.silverChalice}`,
    paddingLeft: MOD_GROUP_ROW_INDENT_PX - 2,
  } as React.CSSProperties,
  tagRow: {
    display: 'grid',
    gap: 2,
    marginTop: -2,
    fontSize: '9px',
    lineHeight: '11px',
    textAlign: 'center',
    color: Colors.silverChalice,
  } as React.CSSProperties,
  collapsedRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    padding: '3px 0',
    cursor: 'pointer',
  } as React.CSSProperties,
  // ModGroupRow's caret: 8px cell, 12px, grey, closed.
  collapsedCaret: {
    flexShrink: 0,
    width: 8,
    textAlign: 'center',
    fontSize: '12px',
    color: Colors.silverChalice,
  } as React.CSSProperties,
  unitTag: {
    fontSize: '11px',
    color: Colors.silverChalice,
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '12px',
  } as React.CSSProperties,
  // ModGroupRow's `key` rule, so the panel's name reads exactly like
  // `bagging_slicing_plates` and every other group name in the pane.
  label: {
    flex: '0 1 auto',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '12px',
    fontWeight: 500,
  } as React.CSSProperties,
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 4,
    margin: '4px 0',
  } as React.CSSProperties,
  strip: {
    display: 'grid',
    gap: 2,
    margin: '4px 0',
  } as React.CSSProperties,
};

// HITL-1732: black means "this option is in the order" — every tile that
// holds at least one slot is black, so what is selected reads at a glance.
// The paint brush (what painting, Fill all and Set all use) is marked
// separately by a 2px amber ring (the 7.x acknowledge colour), on black or,
// for a brush that holds no slot yet, on white.
const tileStyle = (
  isBrush: boolean,
  is86ed: boolean,
  count: number
): React.CSSProperties => {
  const isPicked = count > 0;
  let border = `1px solid ${grey[400]}`;
  if (isBrush) border = `2px solid ${Colors.acknowledge}`;
  else if (isPicked) border = `1px solid ${Colors.mineShaft}`;
  let color: string = Colors.daintree;
  if (is86ed) color = Colors.Thunderbird;
  else if (isPicked) color = Colors.white;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 24,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 2,
    border,
    backgroundColor: isPicked ? Colors.mineShaft : 'transparent',
    color,
    textDecoration: is86ed ? 'line-through' : 'none',
    fontSize: '13px',
    fontWeight: isPicked || isBrush ? 700 : 400,
    cursor: is86ed ? 'default' : 'pointer',
    userSelect: 'none',
  };
};

const countChipStyle = COUNT_CHIP_STYLE;

const cellStyle = (
  isEmpty: boolean,
  isRequired: boolean
): React.CSSProperties => ({
  height: 24,
  minWidth: 0,
  boxSizing: 'border-box',
  borderRadius: 2,
  border: `1px solid ${isEmpty && isRequired ? Colors.Thunderbird : grey[400]}`,
  backgroundColor: isEmpty ? 'transparent' : Colors.alto,
  color: Colors.daintree,
  fontSize: '11px',
  fontWeight: 700,
  // Plain text centring rather than flex: a flex item wider than a narrow
  // cell overflows to the right, so the initials drifted off centre.
  // 22px = 24px less the two 1px borders, which centres it vertically too.
  textAlign: 'center',
  lineHeight: '22px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
});

// A unit the follow-up does not apply to: holds its column, says nothing.
const inertCellStyle: React.CSSProperties = {
  height: 24,
  boxSizing: 'border-box',
  borderRadius: 2,
  border: `1px dashed ${Colors.alto}`,
  cursor: 'default',
};

export const actionStyle = (enabled: boolean): React.CSSProperties => ({
  border: `1px solid ${grey[400]}`,
  borderRadius: 2,
  background: 'transparent',
  color: enabled ? Colors.daintree : Colors.silverChalice,
  // Sized to sit in the header row beside the counter without growing it.
  fontSize: '11px',
  lineHeight: '16px',
  padding: '0 6px',
  cursor: enabled ? 'pointer' : 'default',
});

/**
 * Was this focus reached from the keyboard? `:focus-visible` is the browser's
 * own answer; an engine without it (jsdom) answers no.
 */
const isKeyboardFocus = (target: EventTarget) => {
  try {
    return (target as Element).matches(':focus-visible');
  } catch {
    return false;
  }
};

/** `Cinnamon Raisin` → `CR`, `Plain` → `PL`: what fits in a 1/13 cell. */
export const slotInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

/**
 * Applies one slot's menu defaults the way its row would have: once, while the
 * cart has no entry for the group, through CartItemDetail's own selection
 * callback (so the default queue, scope and "announce nothing" rule all hold).
 * Renders nothing.
 */
export const SlotDefaults = ({
  cartItem,
  modGroup,
  pathToItem,
  selectMenuItemModifier,
}: {
  cartItem: CartItem;
  modGroup: ParsedModifierGroup;
  pathToItem: string;
  selectMenuItemModifier: SelectMenuItemModifier;
}) => {
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const cartModGroup = checkItemInCartItemTree({ cartItem, pathToItem });
  useEffect(() => {
    if (cartModGroup || !modGroup.defaultSelectedItemIds?.length) return;
    applyModGroupDefaults({
      cartItem,
      modGroup,
      pathToItem,
      parentModifier: getSlotParentModifier(pathToItem),
      isBrandMenu: getMenuUnificationFeatureFlag(),
      unavailableItemsByName,
      selectMenuItemModifier,
    });
    // Same trigger as ModGroup's own default effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartModGroup, pathToItem]);
  return null;
};

interface RepeatedSlotPanelProps {
  cartItem: CartItem;
  slotSet: RepeatedSlotSet;
  selectMenuItemModifier: SelectMenuItemModifier;
  /**
   * HITL-1722: drawn under a parent panel. No horizontal inset of its own, so
   * its strip lines up under the parent's; the label and tiles carry the
   * drawer's rail and indent instead.
   */
  isFollowUp?: boolean;
}

const RepeatedSlotPanel = ({
  cartItem,
  slotSet,
  selectMenuItemModifier,
  isFollowUp = false,
}: RepeatedSlotPanelProps) => {
  const write = useRepeatedSlotsWrite(cartItem);
  const { isDraft } = useScopedCartActions();
  const isAgentActionBlocked = useSelector(selectIsAgentActionBlocked);
  const isReadOnly = isAgentActionBlocked && !isDraft;
  // HITL-1696: the agent's CAPITALS choice, applied as ModGroupRow applies it.
  const { pref: displayPref } = useDisplayPreference();
  const isCaps = displayPref.cpCase === 'caps';

  const { groups, paths, stem, isRequired } = slotSet;
  // HITL-1731: no panel re-sorts any more. Grouping like options (for a
  // grouped readback) moved cells the agent never touched, which testers
  // found confusing, so every panel keeps positions as per-unit combos
  // always did: a pick stays in the cell it was put in.
  const keepsPositions = true;
  const { applicable, prefix, parentNames } = slotSet;
  const isApplicable = (index: number) => !applicable || applicable[index];

  // Same order the rows' drawer uses, so the tiles read like the options did.
  const options: ParsedMenuItem[] = useMemo(() => {
    const menuItems = groups[0].menuItems;
    return getMenuUnificationFeatureFlag()
      ? Object.values(menuItems)
      : sortAndFilterMenuItems(menuItems);
  }, [groups]);
  const optionOrder = useMemo(
    () => options.map(({ itemId }) => itemId),
    [options]
  );
  const optionsById = useMemo(
    () => Object.fromEntries(options.map((option) => [option.itemId, option])),
    [options]
  );

  const slots: SlotValue[] = paths.map((pathToItem, index) => {
    if (!isApplicable(index)) return INAPPLICABLE_SLOT;
    const cartModGroup = checkItemInCartItemTree({
      cartItem,
      pathToItem,
    }) as CartModifierGroup | null;
    return Object.values(cartModGroup?.selectedItems ?? {})[0]?.itemId;
  });
  const isReal = (id: SlotValue) => !!id && id !== INAPPLICABLE_SLOT;
  const filled = slots.filter(isReal).length;
  const applicableCount = slots.filter((id) => id !== INAPPLICABLE_SLOT).length;
  const isFull = filled === applicableCount;
  // Only a required set is ever "unmet": an optional set may stay empty.
  const isUnmet = isRequired && !isFull;

  const [brush, setBrush] = useState<string | undefined>();
  // Every gesture's "before", newest last, so Undo can be pressed again and
  // again back to where the agent started. Capped so a long session cannot
  // grow it without bound.
  const [undoStack, setUndoStack] = useState<SlotValue[][]>([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>();
  const clearFlashTimer = () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  };
  useEffect(() => () => clearFlashTimer(), []);

  const flash = useCallback(() => {
    clearFlashTimer();
    setIsFlashing(true);
    flashTimer.current = setTimeout(() => setIsFlashing(false), FLASH_MS);
  }, []);

  // A different line is a different history: Undo must never write one
  // item's earlier slots onto another.
  useEffect(() => setUndoStack([]), [cartItem.cartItemId]);

  const [isFocused, setIsFocused] = useState(false);
  // Clicking a tile focuses the panel too, so focus alone would flash the
  // 1–9 hints on every mouse press. They show once the keys are in use: the
  // panel was reached from the keyboard (Tab), or a digit has been pressed.
  const [isUsingKeys, setIsUsingKeys] = useState(false);
  const showKeyHints = isFocused && isUsingKeys;
  // The cells a drag has crossed so far. Painted as a preview, written once on
  // pointer-up so a stroke is one dispatch, one `order` and one Undo step.
  const [stroke, setStroke] = useState<number[] | null>(null);
  // HITL-1724: the agent opened a collapsed follow-up; keep it open for this
  // line even if it goes back to all-default.
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => setIsExpanded(false), [cartItem.cartItemId]);

  /**
   * Writes `target` over the current slots and remembers them for Undo.
   * True when anything was written.
   */
  const commit = (target: SlotValue[], { recordUndo = true } = {}): boolean => {
    const changes: RepeatedSlotChange[] = [];
    groups.forEach((modGroup, index) => {
      if (!isApplicable(index) || slots[index] === target[index]) return;
      changes.push({
        modGroup,
        pathToItem: paths[index],
        from: slots[index] ? optionsById[slots[index]!] : undefined,
        to: target[index] ? optionsById[target[index]!] : undefined,
      });
    });
    if (!changes.length) return false;
    const summary = describeRepeatedSlots(
      cartItem.name,
      target
        .filter((id) => id !== INAPPLICABLE_SLOT)
        .map((id) => (id ? optionsById[id]?.name : undefined))
    );
    if (!write(changes, summary)) return false;
    setUndoStack((stack) =>
      recordUndo ? [...stack, slots].slice(-UNDO_LIMIT) : stack.slice(0, -1)
    );
    return true;
  };

  const onTileClick = (option: ParsedMenuItem) => {
    if (isReadOnly || option.available === false) return;
    // The brush moves even when the set is full, so a strip the defaults or
    // the AI filled can still be repainted one unit at a time. At ×0 it is
    // white with the amber ring, never black (see tileStyle).
    setBrush(option.itemId);
    const target = addToSlots(
      slots,
      option.itemId,
      optionOrder,
      keepsPositions
    );
    if (!target) {
      flash();
      return;
    }
    commit(target);
  };

  const onRemoveClick = (event: React.MouseEvent, option: ParsedMenuItem) => {
    event.stopPropagation();
    if (isReadOnly) return;
    const target = removeFromSlots(
      slots,
      option.itemId,
      optionOrder,
      keepsPositions
    );
    if (!target || !commit(target)) return;
    // The last one gone: the tile stops being the brush, so its black fill
    // never reads as "still in the order" at ×0.
    if (brush === option.itemId && !target.includes(option.itemId)) {
      setBrush(undefined);
    }
  };

  const onCellPointerDown = (index: number, event: React.PointerEvent) => {
    if (isReadOnly || !brush || event.button > 0 || !isApplicable(index))
      return;
    event.preventDefault();
    // Touch pointers are captured by the element they start on, which would
    // keep every other cell from ever seeing pointerenter.
    const target = event.target as Element;
    if (target.hasPointerCapture?.(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
    setStroke([index]);
  };

  const onCellPointerEnter = (index: number) => {
    if (!isApplicable(index)) return;
    setStroke((current) =>
      current && !current.includes(index) ? [...current, index] : current
    );
  };

  // The pointer can lift anywhere, so listen on the window for the length of
  // the stroke. A ref keeps the listener on the latest slots and brush.
  const finishStrokeRef = useRef<(commitIt: boolean) => void>(() => {});
  finishStrokeRef.current = (commitIt: boolean) => {
    if (commitIt && stroke && brush) {
      // On an optional set, clicking a cell that already holds the brush
      // empties it: the only way to clear one unit's pick in place.
      const isClear =
        !isRequired && stroke.length === 1 && slots[stroke[0]] === brush;
      commit(
        paintSlots(
          slots,
          stroke,
          isClear ? undefined : brush,
          optionOrder,
          keepsPositions
        )
      );
    }
    setStroke(null);
  };
  const isStroking = stroke !== null;
  useEffect(() => {
    if (!isStroking) return undefined;
    const onUp = () => finishStrokeRef.current(true);
    const onCancel = () => finishStrokeRef.current(false);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [isStroking]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (!/^[1-9]$/.test(event.key)) return;
    const option = options[Number(event.key) - 1];
    if (!option) return;
    event.preventDefault();
    event.stopPropagation();
    setIsUsingKeys(true);
    onTileClick(option);
  };

  // HITL-1730: combos start FULL from their menu defaults (8 hamburger
  // entrees, 4 small sides), so "fill the empty slots" had nothing to do and
  // the agent painted every cell to switch them all. On a full panel the
  // button becomes "Set all": every applicable slot to the brush, in place.
  const isSetAll = isFull;
  const applicableIndexes = slots
    .map((id, index) => (id === INAPPLICABLE_SLOT ? -1 : index))
    .filter((index) => index >= 0);
  const canFill =
    !isReadOnly &&
    !!brush &&
    (isSetAll
      ? applicableIndexes.some((index) => slots[index] !== brush)
      : true);
  const onFillAll = () => {
    if (!canFill) return;
    commit(
      isSetAll
        ? paintSlots(
            slots,
            applicableIndexes,
            brush,
            optionOrder,
            keepsPositions
          )
        : fillEmptySlots(slots, brush!, optionOrder, keepsPositions)
    );
  };

  const canUndo = !isReadOnly && undoStack.length > 0;
  const onUndo = () => {
    if (canUndo) commit(undoStack[undoStack.length - 1], { recordUndo: false });
  };

  // HITL-1723: the row's ⓘ, once for the whole family. It speaks the prompt
  // for the unit the agent is about to ask about — the first applicable slot
  // still empty, else the first applicable one — through the same
  // sendModGroupTTSMessage the rows use, so express suppression, the
  // experiment hold-back, the upsell attempt and the Shadow sink all apply
  // exactly as they do there.
  const { sendModGroupTTSMessage } = useTTSMessage();
  const firstEmpty = slots.findIndex((id) => !id);
  const firstApplicable = Math.max(
    0,
    slots.findIndex((id) => id !== INAPPLICABLE_SLOT)
  );
  const promptIndex = firstEmpty >= 0 ? firstEmpty : firstApplicable;
  const promptGroup = groups[promptIndex];
  const promptPath = paths[promptIndex];
  const spokenDescription = useAppSelector((state) =>
    selectModGroupPrompt(
      state,
      {
        cartItemId: cartItem.cartItemId,
        pathToItem: promptPath,
        constraintName: promptGroup.name,
      },
      promptGroup.description
    )
  );
  // The rows' own condition (ModGroupRowView): brand menu, and a description.
  const hasSpokenDescription = !!(
    getMenuUnificationFeatureFlag() &&
    promptGroup.description &&
    spokenDescription
  );

  // HITL-1724: a follow-up collapses to one line only while EVERY applicable
  // unit holds the same value and that value is its group's menu default
  // (extra_patty → extra patty, mod_codes_sub → add). A mix, a change, or an
  // empty cell re-opens it.
  const realValues = slots.filter(isReal) as string[];
  const collapsedValue = realValues[0];
  const isAllOnDefault =
    isFollowUp &&
    isFull &&
    realValues.length > 0 &&
    realValues.every((id) => id === collapsedValue) &&
    slots.every(
      (id, index) =>
        id === INAPPLICABLE_SLOT ||
        (groups[index].defaultSelectedItemIds ?? []).map(String).includes(id!)
    );
  const isCollapsed = isAllOnDefault && !isExpanded;

  const counts = slots.reduce<Record<string, number>>((acc, id) => {
    if (isReal(id)) acc[id!] = (acc[id!] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      style={{
        ...(isFollowUp ? styles.followUpRoot : styles.root),
        ...(isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null),
      }}
      data-testid="repeated-slot-panel"
      // Focusable so 1–9 can be scoped to it; never focused programmatically.
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={(event) => {
        setIsFocused(true);
        if (isKeyboardFocus(event.target)) setIsUsingKeys(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsFocused(false);
          setIsUsingKeys(false);
        }
      }}
    >
      <div
        style={isFollowUp ? styles.followUpBody : styles.body}
        data-testid="repeated-slot-body"
      >
        {groups.map((modGroup, index) =>
          isApplicable(index) ? (
            <SlotDefaults
              key={paths[index]}
              cartItem={cartItem}
              modGroup={modGroup}
              pathToItem={paths[index]}
              selectMenuItemModifier={selectMenuItemModifier}
            />
          ) : null
        )}
        {isCollapsed ? (
          // HITL-1724: every unit still on the menu default — one line, as a
          // closed row reads. Still mounted above (SlotDefaults) and below
          // (follow-ups), so defaults and validation are untouched.
          <div
            role="button"
            tabIndex={0}
            data-testid="repeated-slot-collapsed"
            style={{ ...styles.followUpRail, ...styles.collapsedRow }}
            onClick={() => setIsExpanded(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ')
                setIsExpanded(true);
            }}
          >
            <span style={styles.collapsedCaret}>›</span>
            <span
              style={{
                ...styles.label,
                color: Colors.silverChalice,
                ...(isCaps ? { textTransform: 'uppercase' } : null),
              }}
            >
              {prefix && (
                <span style={{ fontWeight: 700 }}>{`[${prefix}] `}</span>
              )}
              {`${stem}: `}
              <span style={{ color: Colors.daintree, fontWeight: 400 }}>
                {`${
                  optionsById[collapsedValue!]?.name ?? collapsedValue
                } ×${filled}`}
              </span>
            </span>
          </div>
        ) : (
          <>
            <div
              style={isFollowUp ? styles.followUpRail : undefined}
              data-testid={isFollowUp ? 'repeated-slot-rail' : undefined}
            >
              <div style={styles.header}>
                <span
                  data-testid="repeated-slot-label"
                  style={{
                    ...styles.label,
                    // As a row: grey once satisfied, red with `!` while unmet.
                    color: isUnmet ? Colors.Thunderbird : Colors.silverChalice,
                    ...(isCaps ? { textTransform: 'uppercase' } : null),
                  }}
                >
                  {isUnmet ? '! ' : ''}
                  {prefix && (
                    // As a nested row prints its parent: `[triple] extra_patty`.
                    <span
                      data-testid="repeated-slot-prefix"
                      style={{ fontWeight: 700, color: Colors.silverChalice }}
                    >{`[${prefix}] `}</span>
                  )}
                  {stem}
                  {/*
              Where a row prints its `(select 1)`: same 11px / 400, same
              colour as the name, so the count reads as the group's own
              requirement rather than a separate badge.
            */}
                  <span
                    data-testid="repeated-slot-counter"
                    style={{
                      fontSize: '11px',
                      fontWeight: 400,
                      backgroundColor: isFlashing ? Colors.alto : 'transparent',
                      borderRadius: 7,
                      transition: 'background-color 120ms ease',
                    }}
                  >
                    {` (${filled} / ${applicableCount})`}
                  </span>
                </span>
                {hasSpokenDescription && (
                  <Tooltip
                    title={spokenDescription || ''}
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      data-testid="repeated-slot-info"
                      aria-label={`Play description for ${stem}`}
                      sx={{
                        flexShrink: 0,
                        padding: 0,
                        color: Colors.silverChalice,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        sendModGroupTTSMessage(promptGroup, {
                          cartItemId: cartItem.cartItemId,
                          pathToItem: promptPath,
                        });
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <button
                  type="button"
                  style={{ ...actionStyle(canFill), marginLeft: 'auto' }}
                  disabled={!canFill}
                  onClick={onFillAll}
                  data-testid="repeated-slot-fill"
                >
                  {isSetAll ? 'Set all' : 'Fill all'}
                </button>
                <button
                  type="button"
                  style={actionStyle(canUndo)}
                  disabled={!canUndo}
                  onClick={onUndo}
                >
                  Undo
                </button>
              </div>

              <div style={styles.tiles}>
                {options.map((option, optionIndex) => {
                  const is86ed = option.available === false;
                  const isBrush = brush === option.itemId;
                  const count = counts[option.itemId] || 0;
                  return (
                    <div
                      key={option.itemId}
                      role="button"
                      tabIndex={-1}
                      aria-disabled={is86ed}
                      aria-pressed={isBrush}
                      data-testid="repeated-slot-tile"
                      data-option={option.itemId}
                      style={{
                        ...tileStyle(isBrush, is86ed, count),
                        ...(isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null),
                      }}
                      onClick={() => onTileClick(option)}
                    >
                      {showKeyHints && optionIndex < 9 && (
                        <span
                          data-testid="repeated-slot-key"
                          style={{
                            flexShrink: 0,
                            fontSize: '11px',
                            color:
                              count > 0 ? Colors.white : Colors.silverChalice,
                          }}
                        >
                          {optionIndex + 1}
                        </span>
                      )}
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {optionOpensGroups(option) && (
                          // As the drawer marks it: this option opens more choices.
                          <span style={{ color: Colors.silverChalice }}>
                            ›{' '}
                          </span>
                        )}
                        {option.name}
                      </span>
                      {count > 0 && (
                        <>
                          <span style={countChipStyle}>×{count}</span>
                          <span
                            role="button"
                            aria-label={`Remove one ${option.name}`}
                            data-testid="repeated-slot-remove"
                            style={{ fontWeight: 700, padding: '0 2px' }}
                            onClick={(event) => onRemoveClick(event, option)}
                          >
                            −
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                ...styles.strip,
                gridTemplateColumns: `repeat(${slots.length}, minmax(0, 1fr))`,
              }}
            >
              {slots.map((slotId, index) => {
                if (slotId === INAPPLICABLE_SLOT) {
                  return (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      data-testid="repeated-slot-cell"
                      data-inapplicable="true"
                      title={`${index + 1}: does not apply`}
                      style={inertCellStyle}
                    />
                  );
                }
                const id = stroke?.includes(index) ? brush : slotId;
                const name = id ? optionsById[id]?.name ?? id : undefined;
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    title={
                      name ? `${index + 1}: ${name}` : `${index + 1}: empty`
                    }
                    data-testid="repeated-slot-cell"
                    data-value={slotId ?? ''}
                    style={{
                      ...cellStyle(!id, isRequired),
                      ...(isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null),
                    }}
                    onPointerDown={(event) => onCellPointerDown(index, event)}
                    onPointerEnter={() => onCellPointerEnter(index)}
                  >
                    {name ? slotInitials(name) : index + 1}
                  </div>
                );
              })}
            </div>

            {parentNames && (
              // A merged follow-up: which option opened each unit's group.
              <div
                style={{
                  ...styles.tagRow,
                  gridTemplateColumns: `repeat(${slots.length}, minmax(0, 1fr))`,
                }}
              >
                {parentNames.map((name, index) => (
                  <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    data-testid="repeated-slot-tag"
                  >
                    {name ? slotInitials(name) : ''}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {buildFollowUpSets(slotSet, slots, optionsById).map((followUp) =>
          followUp.groups.every(
            (group, index) =>
              !followUp.applicable?.[index] || isSingleChoiceGroup(group)
          ) ? (
            <RepeatedSlotPanel
              key={`${followUp.stem}_${followUp.prefix}`}
              cartItem={cartItem}
              slotSet={followUp}
              selectMenuItemModifier={selectMenuItemModifier}
              isFollowUp
            />
          ) : (
            // Not single-pick (e.g. pick any number): one cell cannot hold it,
            // so each unit gets its ordinary nested row — still mounted, so
            // its defaults apply and the item stays valid.
            <div key={`${followUp.stem}_${followUp.prefix}_rows`}>
              {followUp.groups.map((group, index) =>
                followUp.applicable?.[index] ? (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    data-testid="repeated-slot-followup-row"
                  >
                    <span style={styles.unitTag}>#{index + 1}</span>
                    <ModGroup
                      cartItem={cartItem}
                      modGroup={group}
                      pathToItem={followUp.paths[index]}
                      selectMenuItemModifier={selectMenuItemModifier}
                    />
                  </div>
                ) : null
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default RepeatedSlotPanel;

export { styles as panelStyles };
